import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
    createColumnMenu,
    formatLastUpdatedDate,
    renderlastUpdatedTooltipHeader,
    renderTooltipHeader
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogContent, DialogBody, Button } from "@fluentui/react-components";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { getLastUpdatedDateColorWeeks, NameAndNoDealsBadge } from "../../dashboard-util/dashboard-utils";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import PropTypes from "prop-types";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import { clearDetailStateFromStorage, getAccordionRowRender, getFilteredSortedData, getInitialDetailState, saveDetailStateToStorage, useGroupToggle, PowerBankerNoDealOnFilterState, ProjectIdCell, getStafferMemberProjectsTracker, getMemberSummaryColumns, handleStafferMemberProjectTrackerExport, OverallCapacityCellNonEditable, PowerStafferMemberProjectsEmptyState, renderAvatarWithName, mapPowerExportData, sortProjectsByStatus } from "../dashboard-pui-banker/power-banker-time-log/power-banker-helper";
import "../dashboard-pui-banker/power-banker-time-log/power-banker-time-log.scss";
import { FluentAvatar } from "../../../../components/FluentUI/Fluent-Avatar";
import { FluentBadge } from "../../../../components/FluentUI/Fluent-Badge";
import DealTeamAvatarGroupCell from "../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";

const PowerMemberTrackerModal = ({
    show,
    onHide,
    members,
    user
}) => {
    const [memberState, setMemberState] = useState(() => getInitialDetailState(user?.userID, "powerMemberProjectTracker"));
    const item = members.Projects;
    const member = [members];
    const _projectsExport = useRef(null);
    const _memberExport = useRef(null);
    const filteredData = useMemo(() =>
        getFilteredSortedData(item, memberState.filter, memberState.sort), [item, memberState.filter, memberState.sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const { groupState, toggleGroup, setGroupState } = useGroupToggle();
    const columnMenu = createColumnMenu(item);
    const isEmpty = visibleData?.length === 0;
    const finalGridData = isEmpty ? [{ isEmptyRow: true, showSearchEmptyState: false, isFilterEmptyState: (memberState.filter?.filters?.length ?? 0) > 0 }] : visibleData;
    const hasAnyFilter =
        memberState.filter &&
        Array.isArray(memberState.filter.filters) &&
        memberState.filter.filters.length > 0;
    const canReset = memberState.sort.length > 0 || hasAnyFilter;
    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, memberState.filter),
        [memberState.filter]
    );
    const AccordionRowRender = useMemo(() => getAccordionRowRender(filteredData, groupState, toggleGroup, 13), [filteredData, groupState]);
    const resetDataState = () => {
        const resetState = { sort: [], filter: null };
        setMemberState(resetState);
        clearDetailStateFromStorage(user?.userID, "powerMemberProjectTracker");
    };

    useEffect(() => {
        document.body.classList.toggle("energy-dialog-open", show);
    }, [show]);

    return (
        <>
            <Dialog open={show} modalType="modal" data-testid="deal-resource-modal"
                onOpenChange={(event, data) => {
                    if (!data.open) {
                        onHide();
                    }
                }}>
                <DialogSurface aria-describedby={undefined} className="power-member-tracker-modal deal-resource-modal">
                    <DialogBody>
                        <DialogTitle className="d-flex justify-content-between "
                            action={
                                <DialogTrigger action="close">
                                    <Button
                                        appearance="subtle"
                                        aria-label="close"
                                        icon={<FluentUiIcon name="DismissRegular" />}
                                    />
                                </DialogTrigger>
                            }
                        >
                            <span>{members?.Banker}
                                <p className="subDepartmentTitle">Time Tracker</p>
                            </span>
                            <div className="d-flex align-items-start justify-content-end">
                                <div className="mr-2">
                                    <FluentTooltip content="Reset Filters and Sorting"
                                        target={
                                            <FluentButton

                                                appearance="secondaryWithOutline"
                                                icon={
                                                    <FluentUiIcon
                                                        name="ArrowResetRegular"
                                                        color={canReset ? "#0F6CBD" : "#A0A0A0"}
                                                        size={20}
                                                        disabled={!canReset}
                                                    />
                                                }
                                                onClick={resetDataState}
                                            />
                                        }
                                    >
                                    </FluentTooltip>
                                </div>
                                <FluentTooltip content="Export Data" target={
                                    <FluentButton
                                        appearance="secondaryWithOutline"
                                        icon={
                                            <FluentUiIcon
                                                name="ArrowDownloadRegular"
                                                color="#0F6CBD"
                                                size={20}
                                            />
                                        }
                                        onClick={() =>
                                            handleStafferMemberProjectTrackerExport({ jBRef: _memberExport, projectRef: _projectsExport, Banker: members?.Banker })
                                        }
                                    />
                                }>
                                </FluentTooltip>
                            </div>
                        </DialogTitle>

                        <DialogContent>
                            <div className="detail-grid-scroll-container project-staffer-member-modal">
                                <Grid
                                    data={member}
                                    dataItemKey={"Banker"}
                                    className='member-grid-summary'
                                >
                                    <Column
                                        field="Banker"
                                        title={renderTooltipHeader("Analyst/Associate")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                                        cell={(props) => (
                                            <td className="background-white k-table-td ">
                                                <div className="dashboard-username size-14">
                                                    <FluentAvatar size={28} name={props.dataItem.Banker} className="task-member-user mr-2" />
                                                    <NameAndNoDealsBadge name={props.dataItem.Banker ?? '—'} activeDeals={!props.dataItem?.NoDeals} />
                                                </div>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="Overall Capacity"
                                        title={renderTooltipHeader("Overall Capacity")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("Overall Capacity") ? "filtered" : ""}
                                        cell={(props) => (
                                            (member?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <FluentTooltip
                                                target={<td className="summary-cell k-table-td size-14" ><OverallCapacityCellNonEditable value={props.dataItem.OverallCapacity} /></td>}
                                                text={props.dataItem.OverallCapacity}>
                                            </FluentTooltip>
                                        )}
                                    />
                                    <Column
                                        field="LastWeekHrs"
                                        title={renderTooltipHeader("Hrs Spent Last Wk")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("LastWeekHrs") ? "filtered" : ""}
                                        cell={(props) => (
                                            (member?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.LastWeekHrs ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="WeekendHours"
                                        title={renderTooltipHeader("Wknd Hrs Spent")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                                        cell={(props) => (
                                            (member?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.WeekendHours ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="EstHoursForNextWeek"
                                        title={renderTooltipHeader("Est. Hrs for Nxt Wk")}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("EstHoursForNextWeek") ? "filtered" : ""}
                                        cell={(props) => (
                                            (member?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.EstHoursForNextWeek ?? "—"}
                                            </td>
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    data={finalGridData}
                                    dataItemKey={"ProjectId"}
                                    sortable={true}
                                    sort={memberState.sort}
                                    filter={memberState.filter}
                                    onSortChange={(e) => {
                                        setMemberState({ ...memberState, sort: e.sort })
                                        saveDetailStateToStorage(user.userID, { ...memberState, sort: e.sort }, "", "powerMemberProjectTracker");
                                        setGroupState({ ...groupState });
                                    }}
                                    onFilterChange={(e) => {
                                        saveDetailStateToStorage(user.userID, { ...memberState, filter: e.filter }, "", "powerMemberProjectTracker");
                                        setMemberState({ ...memberState, filter: e.filter })
                                    }}
                                    rowRender={AccordionRowRender}
                                    resizable={true}
                                >
                                    <Column
                                        field="ProjectId"
                                        title={renderTooltipHeader("#")}
                                        width={110}
                                        filter="text"
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
                                        cell={(props) =>
                                            item?.length > 0
                                                ? props.dataItem.isFilterEmptyState
                                                    ? <td colSpan={13} className=" k-table-td ">
                                                        <PowerBankerNoDealOnFilterState resetDataState={resetDataState} />
                                                    </td>
                                                    : <ProjectIdCell {...props} canEdit={user?.currentUser?.privileges?.["Projects"]?.Edit && props.dataItem.IsProjectMember} isNewDeal={props.dataItem.IsNewDeal} />
                                                : <td colSpan={13} >
                                                    <PowerStafferMemberProjectsEmptyState canCreate={user?.currentUser?.privileges?.["Projects"]?.Add ?? false} />
                                                </td>
                                        }
                                    />
                                    <Column
                                        field="ClientName"
                                        title={renderTooltipHeader("Client Name")}
                                        width={150}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("ClientName") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <FluentTooltip
                                                target={<td className="background-white k-table-td"> {props.dataItem.ClientName}</td>}
                                                text={props.dataItem.ClientName}>
                                            </FluentTooltip>
                                        )}
                                    />
                                    <Column
                                        field="ProjectName"
                                        title={renderTooltipHeader("Project Name")}
                                        width={150}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <FluentTooltip
                                                target={<td data-testid="project-name-column" className="background-white k-table-td"> {props.dataItem.ProjectName}{props.dataItem.IsOverdue}   {" "}
                                                    {props.dataItem.IsOverdue && (<FluentBadge
                                                        appearance="filled"
                                                        size="small"
                                                        color="warning"
                                                        text="Overdue"
                                                        shape="rounded"
                                                    />)}
                                                </td>}
                                                text={props.dataItem.ProjectName}>
                                            </FluentTooltip>
                                        )}
                                    />
                                    <Column
                                        field="ProjectType"
                                        title={renderTooltipHeader("Project Type")} 
                                        width={120}
                                        columnMenu={columnMenu}
                                        headerClassName={columnIsFiltered("ProjectType") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <FluentTooltip
                                                target={<td className="background-white k-table-td"> {props.dataItem.ProjectType}</td>}
                                                text={props.dataItem.ProjectType}>
                                            </FluentTooltip>
                                        )}
                                    />
                                    <Column
                                        field="LastWeekHrs"
                                        title={renderTooltipHeader("Hrs Spent Last Wk")}
                                        columnMenu={columnMenu}
                                        width={120}
                                        headerClassName={columnIsFiltered("LastWeekHrs") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.LastWeekHrs ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="WeekendHours"
                                        title={renderTooltipHeader("Wknd Hrs Spent")}
                                        columnMenu={columnMenu}
                                        width={120}
                                        headerClassName={columnIsFiltered("WeekendHours") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.WeekendHours ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="EstHoursForNextWeek"
                                        title={renderTooltipHeader("Est. Hrs for Nxt Wk")}
                                        columnMenu={columnMenu}
                                        width={130}
                                        headerClassName={columnIsFiltered("EstHoursForNextWeek") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="summary-cell k-table-td" >
                                                {props.dataItem.EstHoursForNextWeek ?? "—"}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="LastUpdated"
                                        title={renderlastUpdatedTooltipHeader("Last Updated")}
                                        columnMenu={columnMenu}
                                        width={130}
                                        filter="date"
                                        headerClassName={columnIsFiltered("LastUpdated") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) &&
                                            <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                                                <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="Members"
                                        title={renderTooltipHeader("Project Team")}
                                        filter="text"
                                        columnMenu={columnMenu}
                                        width={130}
                                        headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
                                        cell={(props) =>
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="background-white k-table-td" >
                                                {props.dataItem.Members?.length
                                                    ? <DealTeamAvatarGroupCell members={props.dataItem.Members} />
                                                    : "-"
                                                }
                                            </td>
                                        }
                                    />
                                    <Column
                                        field="CoverageSenior"
                                        title={renderTooltipHeader("Coverage Senior")}
                                        columnMenu={columnMenu}
                                        width={170}
                                        headerClassName={columnIsFiltered("CoverageSenior") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                                {renderAvatarWithName(props.dataItem.CoverageSenior, "teal")}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="ProductSenior"
                                        title={renderTooltipHeader("Product Senior")}
                                        columnMenu={columnMenu}
                                        width={170}
                                        headerClassName={columnIsFiltered("ProductSenior") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                                {renderAvatarWithName(props.dataItem.ProductSenior, "lavender")}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="CoverageVP"
                                        title={renderTooltipHeader("Coverage VP")}
                                        columnMenu={columnMenu}
                                        width={170}
                                        headerClassName={columnIsFiltered("CoverageVP") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                                {renderAvatarWithName(props.dataItem.CoverageVP, "lilac")}
                                            </td>
                                        )}
                                    />
                                    <Column
                                        field="CoverageDirector"
                                        title={renderTooltipHeader("Coverage Director")}
                                        columnMenu={columnMenu}
                                        width={180}
                                        headerClassName={columnIsFiltered("CoverageDirector") ? "filtered" : ""}
                                        cell={(props) => (
                                            (item?.length > 0 && !props.dataItem.isFilterEmptyState) && <td className="summary-cell k-table-td" >
                                                {renderAvatarWithName(props.dataItem.CoverageDirector, "red")}
                                            </td>
                                        )}
                                    />
                                </Grid>
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ExcelExport data={member} ref={_memberExport}>
                {getMemberSummaryColumns()}
            </ExcelExport>
            <ExcelExport data={mapPowerExportData(sortProjectsByStatus(item))} ref={_projectsExport}>
                {getStafferMemberProjectsTracker()}
            </ExcelExport>
        </>
    );
};

PowerMemberTrackerModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    members: PropTypes.object,
    user: PropTypes.object,
};

export default PowerMemberTrackerModal;
